import * as yup from 'yup';
import { ExternalLink } from 'react-feather';
import { gql } from '__generated__';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { GetIntegrationsForProjectQuery as GetIntegrationsForProject } from '__generated__/graphql';

import { assetUrl, facebookPixelRegex, getServerErrors, googleAnalyticsRegex } from '../../common/helpers';
import { useNotifier } from '../../common/Notifications/NotificationProvider';
import UI from '../../common/UI';
import config from '../config';
import useMutation from '../../api/useMutation';
import useQuery from '../../api/useQuery';
import useScroll from '../../common/useScroll';
import useValidation from '../../common/useValidation';

const editIntegrationsSchema = yup.object().shape({
  google_analytics: yup.string().ensure().matches(googleAnalyticsRegex, {
    excludeEmptyString: true,
    name: 'ga_code',
  }),
  facebook_pixel: yup.string().ensure().matches(facebookPixelRegex, {
    excludeEmptyString: true,
    name: 'fbp_code',
  }),
});

const EditIntegrationsMutation = gql(`
  mutation EditIntegrations($input: EditIntegrationsInput!) {
    editIntegrations(input: $input) {
      id
      google_analytics
      facebook_pixel
    }
  }
`);

const getValues = (project: any) => ({
  google_analytics: project.google_analytics,
  facebook_pixel: project.facebook_pixel,
});

export const GetIntegrationsForProjectQuery = gql(`
  query GetIntegrationsForProject($id: ID!) {
    project(id: $id) {
      id
      google_analytics
      facebook_pixel
      mailchimp_connection {
        id
        account
      }
    }
  }
`);

interface EditIntegrationsPageProps {
  projectId: string;
}

const EditIntegrationsPage = ({ projectId }: EditIntegrationsPageProps) => {
  const { data } = useQuery(GetIntegrationsForProjectQuery, {
    variables: {
      id: projectId,
    },
  });

  const project = data?.project;

  if (!project) {
    return null;
  }

  return (
    <UI.Container sc={{ compact: true, px: [0, 4] }}>
      <AnalyticsSection project={project} />
      <UI.HR />
      <MailchimpSection project={project} />
    </UI.Container>
  );
};

const AnalyticsSection = ({ project }: GetIntegrationsForProject) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const { scrollTo } = useScroll();

  const form = useForm({
    values: getValues(project),
  });

  const [editIntegrations, { error, loading }] = useMutation(
    EditIntegrationsMutation, {
      variables: {
        input: {
          ...form.values,
          project: {
            id: project.id,
          },
        },
      },
      refetchQueries: ['Session'],
      onCompleted: ({ editIntegrations: project }) => {
        notifier.success(t('notifications.project_saved'));

        form.reset(getValues(project));
      },
      onError: () => {
        scrollTo('ProjectTab');
      },
    },
  );

  const { valid, errors } = useValidation({
    schema: editIntegrationsSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  if (!project) {
    return null;
  }

  return (
    <UI.Form onSubmit={() => editIntegrations()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H3>{t('tracking_and_analytics')}</UI.H3>

          <UI.InputDescription sc={{ mt: 1 }}>
            {t('tracking_and_analytics_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.Card>
          <UI.FormGrid>
            <UI.InputGroup
              sc={{ valid: !errors.google_analytics, touched: form.touched.google_analytics }}
            >
              <UI.InputLabel>
                <UI.FlexContainer sc={{ alignItems: 'center' }}>
                  <UI.Span sc={{ mr: 1 }}>
                    <img
                      src={assetUrl('images/integrations/googleanalytics.svg')}
                      width={16}
                      alt={t('google_analytics')}
                    />
                  </UI.Span>
                  {t('google_analytics')}
                </UI.FlexContainer>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('enhanced_ecommerce_description')}
                {' '}
                <UI.A
                  href="https://atleta.cc/article/conversion-tracking-using-google-analytics"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('read_about_google_analytics')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
              <UI.Input {...form.text('google_analytics')} placeholder="G-ABCDE12345 or UA-12345678-1" />
              <UI.ErrorMessages
                attribute={t('google_analytics')}
                errors={errors.google_analytics}
              />
            </UI.InputGroup>

            <UI.InputGroup
              sc={{ valid: !errors.facebook_pixel, touched: form.touched.facebook_pixel }}
            >
              <UI.InputLabel>
                <UI.FlexContainer sc={{ alignItems: 'center' }}>
                  <UI.Span sc={{ mr: 1 }}>
                    <img src={assetUrl('images/integrations/meta.svg')} width={20} alt={t('meta')} />
                  </UI.Span>
                  {t('meta_pixel')}
                </UI.FlexContainer>
              </UI.InputLabel>
              <UI.InputDescription>
                {t('meta_pixel_description')}
                {' '}
                <UI.A
                  href="https://atleta.cc/article/conversion-tracking-using-facebook-pixel"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('read_about_meta_pixel')}
                  {' '}
                  <UI.Icon>
                    <ExternalLink />
                  </UI.Icon>
                </UI.A>
              </UI.InputDescription>
              <UI.Input {...form.text('facebook_pixel')} placeholder="112233445566778899" />
              <UI.ErrorMessages
                attribute={t('meta_pixel')}
                errors={errors.facebook_pixel}
              />
            </UI.InputGroup>

            <UI.HR />

            <UI.FormButtons
              form={form}
              submitLabel={t('save')}
              loading={loading}
              valid={valid}
            />
          </UI.FormGrid>
        </UI.Card>
      </UI.FormGrid>
    </UI.Form>
  );
};

const DisconnectMailchimpMutation = gql(`
  mutation DisconnectMailchimp($id: ID!) {
    disconnectMailchimp(id: $id) {
      id
      mailchimp_connection {
        id
        account
      }
    }
  }
`);

const MailchimpSection = ({ project }: GetIntegrationsForProject) => {
  const { t } = useTranslation();

  const [disconnect, { loading }] = (
    useMutation(DisconnectMailchimpMutation, {
      variables: {
        id: project.id,
      },
    })
  );

  return (
    <UI.FormGrid>
      <UI.Div>
        <UI.H3>{t('mailchimp')}</UI.H3>

        <UI.InputDescription sc={{ mt: 1 }}>
          {project.mailchimp_connection ? t('mailchimp_connected_description') : t('mailchimp_description')}
        </UI.InputDescription>
      </UI.Div>
      <UI.Card>
        {project.mailchimp_connection && (
          <UI.FlexContainer sc={{ justifyContent: 'space-between' }}>
            <UI.Span>
              <UI.StatusIndicator
                sc={{ background: 'success', mr: 1 }}
              />
              {t('connected_with')}
              {' '}
              <UI.Icon sc={{ size: 1.25 }}>
                <UI.Icons.Mailchimp />
              </UI.Icon>
              {' '}
              <UI.Strong>
                {project.mailchimp_connection.account}
              </UI.Strong>
            </UI.Span>
            {!loading && (
              <UI.Button onClick={() => disconnect()} sc={{ size: 'sm' }}>
                {t('disconnect')}
              </UI.Button>
            )}
            {loading && <UI.Loader />}
          </UI.FlexContainer>
        )}
        {!project.mailchimp_connection && (
          <UI.AButton
            href={`${config.backendPath}/api/mailchimp/authorize/${project.id}`}
          >
            <UI.Icon sc={{ size: 1.25 }}>
              <UI.Icons.Mailchimp />
            </UI.Icon>
              {' '}
              {t('connect_with', { title: t('mailchimp') })}
          </UI.AButton>
        )}
      </UI.Card>
    </UI.FormGrid>
  );
};

export default EditIntegrationsPage;
